/**
 * custom-share-button.js
 * Shared CCC / Phaser feature (phaser: shared) — native share pill for
 * blocks/custom-share-button.liquid.
 *
 * One tap opens the device share sheet (navigator.share — iOS, Android,
 * Chrome/Edge on Windows, Safari on macOS). Browsers without it (desktop
 * Firefox) copy the link instead and confirm with a short "Link copied"
 * swap on the pill. The no-JS fallback is the trigger's mailto: href.
 *
 * Shared URL = the block's canonical product URL plus the live ?variant=
 * param, so a customer who has picked a colour shares that colour — but
 * tracking params and the like never travel with it.
 */

class ShareButton extends HTMLElement {
  connectedCallback() {
    this._trigger = this.querySelector('[data-ccc-share-trigger]');
    this._label = this.querySelector('[data-ccc-share-label]');
    this._status = this.querySelector('[data-ccc-share-status]');
    if (!this._trigger || this._bound) return;
    this._bound = true;

    this._trigger.addEventListener('click', (event) => {
      event.preventDefault();
      this._share();
    });
  }

  disconnectedCallback() {
    window.clearTimeout(this._revertTimer);
  }

  get _shareUrl() {
    const base = this.dataset.shareUrl || `${window.location.origin}${window.location.pathname}`;
    const variant = new URLSearchParams(window.location.search).get('variant');
    return variant ? `${base}?variant=${encodeURIComponent(variant)}` : base;
  }

  async _share() {
    const url = this._shareUrl;
    const title = this.dataset.shareTitle || document.title;

    if (navigator.share) {
      try {
        await navigator.share({ title, url });
        return;
      } catch (error) {
        // Customer closed the sheet — fine, done here.
        if (error instanceof Error && error.name === 'AbortError') return;
        // Anything else (NotAllowedError etc.): fall through to copy.
      }
    }

    await this._copy(url);
  }

  async _copy(url) {
    let copied = false;
    try {
      await navigator.clipboard.writeText(url);
      copied = true;
    } catch {
      copied = this._legacyCopy(url);
    }
    if (copied) this._confirmCopied();
  }

  /** execCommand path for the few engines that block the async clipboard. */
  _legacyCopy(url) {
    const scratch = document.createElement('textarea');
    scratch.value = url;
    scratch.setAttribute('readonly', '');
    scratch.style.position = 'fixed';
    scratch.style.opacity = '0';
    document.body.appendChild(scratch);
    scratch.select();
    let copied = false;
    try {
      copied = document.execCommand('copy');
    } catch {
      copied = false;
    }
    scratch.remove();
    return copied;
  }

  _confirmCopied() {
    if (!this._label) return;
    if (!this._idleText) this._idleText = this._label.textContent;
    const copiedText = this.dataset.copiedText || 'Link copied';

    this._label.textContent = copiedText;
    this._trigger.classList.add('ccc-share__trigger--copied');
    if (this._status) this._status.textContent = copiedText;

    window.clearTimeout(this._revertTimer);
    this._revertTimer = window.setTimeout(() => {
      this._label.textContent = this._idleText;
      this._trigger.classList.remove('ccc-share__trigger--copied');
      if (this._status) this._status.textContent = '';
    }, 2000);
  }
}

if (!customElements.get('ccc-share-button')) {
  customElements.define('ccc-share-button', ShareButton);
}
